import {BaseBadge, BaseButton, BaseCard, BaseInput, BaseModal, BaseTable} from "@/components";
import type {Column} from "@/components/BaseTable/BaseTable";
import {
  useCreateFxRateConfig,
  useDeleteFxRateConfig,
  useFxRateConfigs,
  useUpdateFxRateConfig,
} from "@/hooks/useFxRateConfigs";
import type {FxRateConfig} from "@/services/fx-rate-configs.service";
import {Pause, Pencil, Play, Trash2} from "lucide-react";
import {useState} from "react";

const formatDate = (iso: string | null): string => {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("es-CL", {dateStyle: "short", timeStyle: "short"}).format(new Date(iso));
};

export function FxRateConfigs() {
  const {data: configs, isLoading} = useFxRateConfigs();
  const updateConfig = useUpdateFxRateConfig();
  const deleteConfig = useDeleteFxRateConfig();
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<FxRateConfig | null>(null);

  const items = configs ?? [];

  const columns: Column<FxRateConfig>[] = [
    {
      key: "pair",
      header: "Par",
      render: row => (
        <span className="text-sm font-semibold text-(--color-text-primary)">
          {row.from_currency} → {row.to_currency}
        </span>
      ),
    },
    {
      key: "api_source",
      header: "Fuente API",
      render: row => <span className="text-sm text-(--color-text-secondary)">{row.api_source}</span>,
    },
    {
      key: "spread_adjustment_pct",
      header: "Spread Ajuste",
      render: row => (
        <span className="text-sm font-mono tabular-nums text-(--color-text-primary)">{row.spread_adjustment_pct}%</span>
      ),
    },
    {
      key: "auto_update_enabled",
      header: "Auto-Update",
      render: row => (
        <BaseBadge
          variant={row.auto_update_enabled ? "info" : "neutral"}
          label={row.auto_update_enabled ? `Cada ${row.update_interval_minutes}m` : "Manual"}
          dot
        />
      ),
    },
    {
      key: "last_api_rate",
      header: "Última Tasa API",
      render: row => (
        <div>
          <p className="text-sm font-mono tabular-nums text-(--color-text-primary)">{row.last_api_rate ?? "—"}</p>
          <p className="text-xs text-(--color-text-tertiary)">{formatDate(row.last_api_update)}</p>
        </div>
      ),
    },
    {
      key: "is_active",
      header: "Estado",
      render: row => (
        <BaseBadge
          variant={row.is_active ? "success" : "neutral"}
          label={row.is_active ? "Activo" : "Inactivo"}
          dot
        />
      ),
    },
    {
      key: "actions",
      header: "Acciones",
      align: "right",
      render: row => (
        <div className="flex items-center gap-1 justify-end">
          <BaseButton
            variant="ghost"
            size="sm"
            onClick={() => setEditing(row)}
            title="Editar"
          >
            <Pencil size={14} />
          </BaseButton>
          <BaseButton
            variant="ghost"
            size="sm"
            onClick={() => updateConfig.mutate({id: row.id, body: {is_active: !row.is_active}})}
            title={row.is_active ? "Desactivar" : "Activar"}
          >
            {row.is_active ? <Pause size={14} /> : <Play size={14} />}
          </BaseButton>
          <BaseButton
            variant="ghost"
            size="sm"
            onClick={() => {
              if (confirm(`¿Eliminar config ${row.from_currency}→${row.to_currency}?`)) deleteConfig.mutate(row.id);
            }}
            title="Eliminar"
          >
            <Trash2 size={14} />
          </BaseButton>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-(--color-text-primary)">FX Rate Configs</h1>
          <p className="text-sm text-(--color-text-secondary) mt-1">
            {items.length} configuraciones — Automatización de tasas desde APIs externas
          </p>
        </div>
        <BaseButton
          variant="primary"
          size="md"
          onClick={() => setShowCreate(true)}
        >
          + Nueva config
        </BaseButton>
      </div>

      <BaseCard padding="none">
        <BaseTable<FxRateConfig>
          data={items}
          columns={columns}
          loading={isLoading}
          emptyMessage="No hay configuraciones FX."
          onRowClick={row => setEditing(row)}
        />
      </BaseCard>

      {showCreate && <CreateFxRateConfigModal onClose={() => setShowCreate(false)} />}
      {editing && (
        <EditFxRateConfigModal
          config={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function CreateFxRateConfigModal({onClose}: {onClose: () => void}) {
  const createConfig = useCreateFxRateConfig();
  const [form, setForm] = useState({
    from_currency: "",
    to_currency: "",
    api_source: "apilayer",
    spread_adjustment_pct: "2.0000",
    update_interval_minutes: "60",
  });
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setError("");
    if (!form.from_currency || !form.to_currency) {
      setError("Las monedas origen y destino son requeridas");
      return;
    }
    try {
      await createConfig.mutateAsync({
        from_currency: form.from_currency.toUpperCase(),
        to_currency: form.to_currency.toUpperCase(),
        api_source: form.api_source,
        spread_adjustment_pct: form.spread_adjustment_pct,
        auto_update_enabled: true,
        update_interval_minutes: Number(form.update_interval_minutes),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al crear la configuración");
    }
  };

  const set = (key: string, value: string) => setForm(prev => ({...prev, [key]: value}));

  return (
    <BaseModal
      open
      onClose={onClose}
      title="Nueva Configuración FX"
      size="md"
      footer={
        <>
          <BaseButton
            variant="ghost"
            size="md"
            onClick={onClose}
          >
            Cancelar
          </BaseButton>
          <BaseButton
            variant="primary"
            size="md"
            loading={createConfig.isPending}
            onClick={handleSubmit}
          >
            Crear
          </BaseButton>
        </>
      }
    >
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4"
      >
        <div className="grid grid-cols-2 gap-3">
          <BaseInput
            label="Moneda Origen"
            placeholder="USD"
            value={form.from_currency}
            onChange={e => set("from_currency", e.target.value.toUpperCase())}
          />
          <BaseInput
            label="Moneda Destino"
            placeholder="VES"
            value={form.to_currency}
            onChange={e => set("to_currency", e.target.value.toUpperCase())}
          />
        </div>
        <BaseInput
          label="Fuente API"
          value={form.api_source}
          onChange={e => set("api_source", e.target.value)}
        />
        <div className="grid grid-cols-2 gap-3">
          <BaseInput
            label="Spread Ajuste %"
            type="number"
            step="0.01"
            value={form.spread_adjustment_pct}
            onChange={e => set("spread_adjustment_pct", e.target.value)}
          />
          <BaseInput
            label="Intervalo (minutos)"
            type="number"
            value={form.update_interval_minutes}
            onChange={e => set("update_interval_minutes", e.target.value)}
          />
        </div>
        {error && <p className="text-sm text-(--color-danger)">{error}</p>}
      </form>
    </BaseModal>
  );
}

function EditFxRateConfigModal({config: cfg, onClose}: {config: FxRateConfig; onClose: () => void}) {
  const updateConfig = useUpdateFxRateConfig();
  const [form, setForm] = useState({
    spread_adjustment_pct: cfg.spread_adjustment_pct,
    auto_update_enabled: cfg.auto_update_enabled,
    update_interval_minutes: String(cfg.update_interval_minutes),
  });
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setError("");
    try {
      await updateConfig.mutateAsync({
        id: cfg.id,
        body: {
          spread_adjustment_pct: form.spread_adjustment_pct,
          auto_update_enabled: form.auto_update_enabled,
          update_interval_minutes: Number(form.update_interval_minutes),
        },
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al actualizar");
    }
  };

  const set = (key: string, value: string | boolean) => setForm(prev => ({...prev, [key]: value}));

  return (
    <BaseModal
      open
      onClose={onClose}
      title={`Editar — ${cfg.from_currency}/${cfg.to_currency}`}
      size="md"
      footer={
        <>
          <BaseButton
            variant="ghost"
            size="md"
            onClick={onClose}
          >
            Cancelar
          </BaseButton>
          <BaseButton
            variant="primary"
            size="md"
            loading={updateConfig.isPending}
            onClick={handleSubmit}
          >
            Guardar
          </BaseButton>
        </>
      }
    >
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4"
      >
        <div className="grid grid-cols-3 gap-3 p-3 rounded-md bg-(--color-surface-2) text-xs text-(--color-text-secondary)">
          <div>
            <span className="font-medium">Origen:</span> {cfg.from_currency}
          </div>
          <div>
            <span className="font-medium">Destino:</span> {cfg.to_currency}
          </div>
          <div>
            <span className="font-medium">Fuente:</span> {cfg.api_source}
          </div>
        </div>
        <BaseInput
          label="Spread Ajuste %"
          type="number"
          step="0.01"
          value={form.spread_adjustment_pct}
          onChange={e => set("spread_adjustment_pct", e.target.value)}
        />
        <div className="grid grid-cols-2 gap-3">
          <BaseInput
            label="Intervalo (minutos)"
            type="number"
            value={form.update_interval_minutes}
            onChange={e => set("update_interval_minutes", e.target.value)}
          />
          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2 text-sm text-(--color-text-secondary)">
              <input
                type="checkbox"
                checked={form.auto_update_enabled}
                onChange={e => set("auto_update_enabled", e.target.checked)}
                className="accent-(--color-primary)"
              />
              Auto-update habilitado
            </label>
          </div>
        </div>
        {cfg.last_api_rate && (
          <div className="p-3 rounded-md bg-(--color-surface-2) text-xs text-(--color-text-tertiary)">
            <span className="font-medium">Última tasa API:</span> {cfg.last_api_rate} —{" "}
            {formatDate(cfg.last_api_update)}
          </div>
        )}
        {error && <p className="text-sm text-(--color-danger)">{error}</p>}
      </form>
    </BaseModal>
  );
}
